import { TransactionView } from "@bitwarden/web-vault/app/models/view/transaction/transaction.view";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { TransactionStoreModel } from "@bitwarden/web-vault/app/models/store/transaction.store.model";
import { AllocationUtils } from "@bitwarden/web-vault/app/models/store/allocations/allocation.utils";
import { TransactionStatusEnum } from "@bitwarden/web-vault/app/models/enum/transactionType";
import { TransactionDirection } from "@bitwarden/web-vault/app/models/enum/transactionDirection";
import { GlossDateFactory } from "@bitwarden/web-vault/app/models/data/shared/gloss-date/gloss-date.factory";
import { Origin } from "@bitwarden/web-vault/app/models/types/general-types";

export type PluginTransaction = {
  transactionId: string;
  transactionTime: string;
  amount: number;
  currency: string;
  description: string;
  status: string;
  balance: number;
};

export type PluginTransactionsResponse = {
  transaction: {
    info: {
      nextCursor: string;
      hasNext: boolean;
    };
    nodes: PluginTransaction[];
  }[];
};

export class PluginTransactionsMap {
  // TODO : save categories and pass it to createAllocationsFromCategoriesAndClassifications
  static toTransactionView(
    pluginTransactions: PluginTransaction[],
    accountView: any,
  ): TransactionView[] {
    const transactions: TransactionView[] = pluginTransactions.map(
      (pluginTransaction: PluginTransaction) => {
        // Parse transaction time from format "MM-DD-YYYY HH:mm" to ISO string
        const timeZone = accountView.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
        let transactionDateISO: string | null = null;
        let postDateISO: string | null = null;

        if (pluginTransaction.transactionTime) {
          const raw = pluginTransaction.transactionTime.trim();
          if (raw.includes("T")) {
            // ISO 8601 format (e.g. 2025-12-22T03:21:51.000Z)
            const transactionDate = new Date(raw);
            if (!isNaN(transactionDate.getTime())) {
              transactionDateISO = transactionDate.toISOString();
              postDateISO = transactionDateISO;
            }
          } else {
            // Legacy format "MM-DD-YYYY HH:mm"
            const [datePart, timePart] = raw.split(" ");
            if (datePart && timePart) {
              const [month, day, year] = datePart.split("-");
              const [hours, minutes] = timePart.split(":");
              const transactionDate = new Date(
                Date.UTC(
                  parseInt(year, 10),
                  parseInt(month, 10) - 1,
                  parseInt(day, 10),
                  parseInt(hours, 10),
                  parseInt(minutes, 10) || 0,
                ),
              );
              if (!isNaN(transactionDate.getTime())) {
                transactionDateISO = transactionDate.toISOString();
                postDateISO = transactionDateISO;
              }
            }
          }
        }

        const transactionStoreModel: TransactionStoreModel = {
          acId: accountView.id,
          ori: "plugin",
          bIB: undefined,
          bIBO: undefined,
          alc: AllocationUtils.createAllocationsFromCategoriesAndClassifications(
            [],
            [],
            Number(pluginTransaction.amount),
          ).map((a) => a.toAllocationAsObjectType()),
          dc: new Date().toISOString(),
          dfn: TransactionStatusEnum.transaction,
          dir: pluginTransaction.amount > 0 ? TransactionDirection.In : TransactionDirection.Out,
          dm: new Date().toISOString(),
          dsc: pluginTransaction.description,
          id: pluginTransaction.transactionId,
          knd: "transaction",
          lTid: [],
          xLid: [],
          sts: pluginTransaction.status as any,
          qty: {
            qty: {
              amt: Math.abs(Number(pluginTransaction.amount)),
              prcs: 8,
              sym: accountView.currency,
            },
            cvrt: 1,
            cvsym: "",
            ccy: accountView.currency,
          },
          srId: null as any,
          tDt: transactionDateISO
            ? GlossDateFactory.createFromISOString(
                transactionDateISO,
                false,
                timeZone,
              ).toGlossDateAsObject()
            : GlossDateFactory.createFromISOString(
                postDateISO,
                false,
                timeZone,
              ).toGlossDateAsObject(),
          pDt: GlossDateFactory.createFromISOString(
            postDateISO,
            false,
            timeZone,
          ).toGlossDateAsObject(),
          v: 9,
          vid: "",
          vlPc: 0,
        };
        return new TransactionView(transactionStoreModel);
      },
    );

    return transactions;
  }
}
